import React, { useState } from "react";

import { fromPairs, merge, stubTrue } from "lodash/fp";

import type { SelectChangeEvent } from "@mui/material";
import Typography from "@mui/material/Typography";

import ReadAssistantPreviewModal from "@app/analytics/account/components/ReadAssistantPreviewModal";
import Setting from "@app/analytics/account/components/Setting";
import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingMultiSelect from "@app/analytics/account/components/SettingMultiSelect";
import { Divider } from "@app/analytics/account/components/styles";
import ZoomOnlyChip from "@app/analytics/account/components/ZoomOnlyChip";
import type { AssistantDisplayOptionValue } from "@app/analytics/account/types";
import { getSettingDisabledOpts } from "@app/analytics/account/UserPreferences/utils";
import {
  BOT_CONFIG_MULTI_SELECT_PROPS,
  getBotConfigDisplaySettings,
  getBotConfigRadioValue,
  getEnabledBotScreens,
  getUpdatedSettingsFromRadioValue,
} from "@app/analytics/account/utils";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { BotConfig, User, UserSettings } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import Dropdown from "@rui/Dropdown";
import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import { Link } from "@rui/Link";
import { Column, Row } from "@rui/Stack";

import ReadAssistantNameSelector from "./ReadAssistantNameSelector";

const DEFAULT_BOT_CONFIG: BotConfig = {
  display: false,
  meetingScore: true,
  timer: true,
  talkTime: true,
};

const ASSISTANT_DISPLAY_OPTIONS: {
  value: AssistantDisplayOptionValue;
  label: string;
  enabled: (inWorkspace: boolean) => boolean;
}[] = [
  {
    value: "display_off",
    label: "Assistant name only",
    enabled: stubTrue,
  },
  {
    value: "display_on_metrics_off",
    label: "Logo (Read or Custom)",
    enabled: (inWorkspace) => inWorkspace,
  },
  {
    value: "display_on_metrics_on",
    label: "Meeting metrics",
    enabled: stubTrue,
  },
];

const getMultiSelectProps = (canEditSettings: boolean) => {
  const enabledSettings = getEnabledBotScreens(DEFAULT_BOT_CONFIG);

  const items = BOT_CONFIG_MULTI_SELECT_PROPS.items.map((item) => ({
    ...item,
    ...getSettingDisabledOpts(canEditSettings),
  }));

  return {
    selectAll: {
      ...BOT_CONFIG_MULTI_SELECT_PROPS.selectAll,
      isDefault: enabledSettings.length === items.length,
      ...getSettingDisabledOpts(canEditSettings),
    },
    items,
  };
};

const AssistantDisplaySettings = ({
  onChange,
  user,
  userSettings,
}: {
  onChange: (settings: Partial<UserSettings>) => void;
  user: User;
  userSettings: UserSettings;
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { inWorkspace } = useWorkspace();
  const { canEditBotConfig } = useFeatureAccess();

  const disabledOpts = getSettingDisabledOpts(canEditBotConfig);

  const defaultOptionValue = getBotConfigRadioValue(
    DEFAULT_BOT_CONFIG,
    inWorkspace
  );

  const botConfig = getBotConfigDisplaySettings(
    userSettings?.botConfig ?? DEFAULT_BOT_CONFIG
  );
  const multiSelectValues = getEnabledBotScreens(botConfig);
  const optionValue = getBotConfigRadioValue(botConfig, inWorkspace);

  const multiSelectProps = inWorkspace
    ? getMultiSelectProps(!disabledOpts.disabled)
    : BOT_CONFIG_MULTI_SELECT_PROPS;

  const updateSettings = (settings: Partial<BotConfig>) => {
    const mergedConfig = merge({ ...botConfig }, settings);
    onChange({ botConfig: mergedConfig });
  };

  const handleRadioChange = (e: SelectChangeEvent) => {
    if (disabledOpts.disabled) return;

    const value = e.target.value as AssistantDisplayOptionValue;
    return updateSettings(getUpdatedSettingsFromRadioValue(value, inWorkspace));
  };

  const handleCheckboxChange = (newValues: string[]) => {
    const newSettings = fromPairs(
      multiSelectProps.items.map((item) => [
        item.value,
        newValues.includes(item.value),
      ])
    );
    updateSettings(newSettings);
  };

  const enabledAssistantOptions = ASSISTANT_DISPLAY_OPTIONS.filter((opt) =>
    opt.enabled(inWorkspace)
  );

  const showBotConfigToggles =
    (!disabledOpts.disabled || !inWorkspace) &&
    optionValue === "display_on_metrics_on";

  return (
    <>
      <Setting
        title="Assistant Display"
        subtitle={
          <>
            When your Assistant joins a meeting, choose what is shown in its
            participant window.{" "}
            <Link variant="bodySecondaryMed" onClick={() => setModalOpen(true)}>
              Compare these settings
            </Link>
            .
          </>
        }
        sx={{ pb: 0 }}
      >
        <SettingContent spacing={2}>
          <Column sx={{ color: "shades.neutral.800" }} spacing={1}>
            <Row variant="left" spacing={1}>
              <Typography variant="bodyPrimaryMed">
                Assistant participant window
              </Typography>
              <ZoomOnlyChip />
            </Row>
            <Dropdown
              name="assistant-display"
              onChange={handleRadioChange}
              selectedItem={getSelectedFromOptions(
                enabledAssistantOptions,
                optionValue
              )}
              disabled={disabledOpts.disabled}
              tooltip={disabledOpts.tooltip}
              defaultValue={defaultOptionValue}
              sx={{ maxWidth: "500px" }}
            >
              {enabledAssistantOptions.map((item) => (
                <DropdownMenuItem
                  key={item.value}
                  {...item}
                  isDefault={defaultOptionValue === item.value}
                  selected={item.value === optionValue}
                />
              ))}
            </Dropdown>
            {showBotConfigToggles && (
              <>
                <Divider sx={{ pt: 1 }} />
                <Column spacing={1}>
                  <Typography variant="bodyPrimaryMed">
                    When meeting metrics are enabled, show:
                  </Typography>
                  <SettingMultiSelect
                    {...multiSelectProps}
                    value={multiSelectValues}
                    onChange={handleCheckboxChange}
                  />
                </Column>
              </>
            )}
          </Column>
          <Divider />
          <ReadAssistantNameSelector inWorkspace={inWorkspace} user={user} />
        </SettingContent>
      </Setting>
      <ReadAssistantPreviewModal
        onClose={() => setModalOpen(false)}
        open={modalOpen}
      />
    </>
  );
};

export default AssistantDisplaySettings;
